<script lang="ts">
  import { docData } from 'rxfire/firestore';
  import { of, startWith } from 'rxjs';
  import { roundRef, tournamentRef } from '../firebase/firestore/collections';
  import { formatDayBreadcrumb } from '../utils/roundCalendar';
  import { isActive, navigate, route } from '../utils/router';

  let tournamentId = $derived(route.params.tournamentId ?? null);
  let dayKey = $derived(route.params.dayKey ?? null);
  let roundId = $derived(route.params.roundId ?? null);
  let questionId = $derived(
    (route.params as Record<string, string | undefined>).questionId ?? null
  );
  let isLiveSolution = $derived(
    isActive('/tournament/:tournamentId/live/:roundId/setsolution')
  );
  let isTournamentQuestion = $derived(
    questionId != null && dayKey == null && !isLiveSolution
  );
  let isDailyLeaderboard = $derived(
    isActive('/tournament/:tournamentId/daily-leaderboard')
  );
  let isWinners = $derived(isActive('/tournament/:tournamentId/winners'));
  let isEdit = $derived(isActive('/tournament/:tournamentId/edit'));
  let isJsonEdit = $derived(
    isActive('/tournament/:tournamentId/questions/jsonedit')
  );

  let tournament = $derived(
    tournamentId
      ? docData(tournamentRef(tournamentId)).pipe(startWith(null))
      : of(null)
  );

  let round = $derived(
    roundId ? docData(roundRef(roundId)).pipe(startWith(null)) : of(null)
  );

  let hasSubPage = $derived(
    dayKey ||
      isLiveSolution ||
      isTournamentQuestion ||
      isDailyLeaderboard ||
      isWinners ||
      isEdit ||
      isJsonEdit
  );
</script>

<nav class="breadcrumbs" aria-label="Navigasjon">
  <button class="crumb" onclick={() => navigate('/')}>Turneringer</button>
  {#if tournamentId}
    {#if $tournament}
      <span class="sep" aria-hidden="true">›</span>

      {#if hasSubPage}
        <button
          class="crumb"
          onclick={() =>
            navigate('/tournament/:tournamentId', {
              params: { tournamentId: tournamentId! },
            })}>{$tournament.name}</button
        >
      {:else}
        <span class="crumb current" aria-current="page">{$tournament.name}</span
        >
      {/if}
    {/if}

    {#if isLiveSolution}
      <span class="sep" aria-hidden="true">›</span>
      <button
        class="crumb"
        onclick={() =>
          navigate('/tournament/:tournamentId/live', {
            params: { tournamentId: tournamentId! },
          })}>Live</button
      >
    {/if}

    {#if dayKey}
      <span class="sep" aria-hidden="true">›</span>

      {#if roundId}
        <button
          class="crumb"
          onclick={() =>
            navigate('/tournament/:tournamentId/interactionday/:dayKey', {
              params: { tournamentId: tournamentId!, dayKey },
            })}>{formatDayBreadcrumb(dayKey)}</button
        >
      {:else}
        <span class="crumb current" aria-current="page"
          >{formatDayBreadcrumb(dayKey)}</span
        >
      {/if}
    {/if}

    {#if roundId && $round}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">{$round.name}</span>
    {/if}

    {#if isTournamentQuestion}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">Turneringsspørsmål</span>
    {/if}

    {#if isDailyLeaderboard}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">Daglig ledertavle</span>
    {/if}

    {#if isWinners}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">Vinnere</span>
    {/if}

    {#if isEdit}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">Rediger</span>
    {/if}

    {#if isJsonEdit}
      <span class="sep" aria-hidden="true">›</span>
      <span class="crumb current" aria-current="page">JSON-redigering</span>
    {/if}
  {/if}
</nav>

<style>
  .breadcrumbs {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 8px 0 12px;
    font-size: 0.82rem;
    flex-wrap: wrap;
  }

  .sep {
    color: var(--color-text);
    padding: 0 2px;
    user-select: none;
  }

  .crumb {
    background: none;
    border: none;
    padding: 2px 6px;
    border-radius: 4px;
    cursor: pointer;
    color: #555;
    font-size: inherit;

    &:hover {
      background: var(--hover);
      color: var(--color-text);
    }
  }

  .crumb.current {
    font-weight: 600;
    color: var(--color-text);
    cursor: default;
  }
</style>
